// Returns tour - the "My Returns" list embed and the "Log a return" form embed.
import { exists, visible, loggedIn } from './util.js';

function onList() { return exists('[data-pf-returns] [data-tour="returns-header"]'); }
function onForm() { return exists('[data-pf-log-return-form] .pf-return-form'); }
function signedIn() { return loggedIn() && !exists('[data-pf-returns] a[href="/account/login"]'); }
function hasReturns() { return exists('[data-pf-returns] [data-tour="returns-row"]'); }
function firstRow() { return visible('[data-pf-returns] [data-tour="returns-row"]'); }

export default {
  id: 'returns',
  version: 1,
  title: 'Returns',
  icon: 'returns',
  duration: '30 sec',
  available: function () { return exists('[data-pf-returns]') || exists('[data-pf-log-return-form]'); },
  readySelector: '[data-pf-returns] [data-tour="returns-header"], [data-pf-log-return-form] .pf-return-form',
  invite: {
    kicker: 'Returns',
    title: 'Returning a part? It takes 2 minutes',
    body: 'See how to log a return against your order and follow it through to your refund.',
  },
  steps: [
    {
      title: function () { return onForm() ? 'Log a return in a few steps' : 'Returns, made simple'; },
      body: function () {
        return onForm()
          ? 'Choose the order and the items, tell us why, and add photos if they help. We take it from there.'
          : 'Log a return against any paid, completed order and follow its progress here, from request to refund.';
      },
      icon: 'returns',
      hero: 'intro',
    },

    // ── List ─────────────────────────────────────────────────────────────
    {
      when: function () { return onList() && !signedIn(); },
      target: '[data-pf-returns] a[href="/account/login"]',
      icon: 'lock',
      title: 'Sign in first',
      body: 'Returns are linked to your orders, so sign in to log or track one.',
      optional: true,
    },
    {
      when: function () { return onList() && signedIn(); },
      target: '[data-pf-returns] [data-tour="returns-log-btn"]',
      icon: 'plus',
      title: 'Start a return',
      body: 'Opens the return form. Only paid, completed orders can be returned.',
    },
    {
      when: function () { return onList() && signedIn() && !hasReturns(); },
      target: '[data-pf-returns] [data-tour="returns-empty"]',
      icon: 'list',
      title: 'Your returns appear here',
      body: 'Nothing logged yet. Once you log a return, it shows here with its live status.',
      optional: true,
    },
    {
      when: function () { return onList() && hasReturns(); },
      target: '[data-pf-returns] [data-tour="returns-table"]',
      icon: 'list',
      title: 'Every return you’ve logged',
      body: 'The order, the date you logged it, the items and where it is in the process.',
      padding: 4,
    },
    {
      when: function () { return onList() && hasReturns(); },
      target: function () { var r = firstRow(); return r ? r.querySelector('.oh-badge') : null; },
      icon: 'badge',
      title: 'Live status',
      body: 'Follow each return from <b>Requested</b> to <b>Approved</b>, <b>Received</b> and <b>Refunded</b>. The badge updates as our team works on it.',
      optional: true,
    },
    {
      when: function () { return onList() && hasReturns(); },
      target: function () { var r = firstRow(); return r ? r.querySelector('[data-tour="returns-view"]') : null; },
      icon: 'eye',
      title: 'See the details',
      body: 'Open a return to see its items, reason, photos, and any drop-off or collection instructions.',
      optional: true,
    },

    // ── Log-a-return form ────────────────────────────────────────────────
    {
      when: function () { return onForm() && exists('[data-tour="return-policy"]'); },
      target: '[data-tour="return-policy"]',
      icon: 'shield',
      title: 'Know the policy',
      body: 'Check what can be returned and the time limits before you start.',
      optional: true,
      waitFor: 600,
    },
    {
      when: onForm,
      target: '[data-tour="return-order"]',
      icon: 'doc',
      title: 'Pick the order',
      body: 'Choose the order the part came from. Only eligible orders are listed.',
      interactive: {
        on: 'change',
        hint: 'Your turn: choose an order',
        done: 'Got it. The items from that order appear below.',
        delay: 1000,
      },
    },
    {
      when: function () { return onForm() && exists('[data-tour="return-items"]'); },
      target: '[data-tour="return-items"]',
      icon: 'package',
      title: 'Choose the items and quantities',
      body: 'Tick each part you are returning and set how many. Items that cannot be returned are labelled.',
      optional: true,
      waitFor: 1200,
    },
    {
      when: onForm,
      target: '[data-tour="return-reason"]',
      icon: 'help',
      title: 'Tell us why',
      body: 'The reason helps us process it faster. A wrong or faulty part may need a few extra checks.',
    },
    {
      when: onForm,
      target: '[data-tour="return-comment"]',
      icon: 'message',
      title: 'Anything else?',
      body: 'Add any detail that helps, such as what happened on fitment or the condition of the packaging.',
      optional: true,
    },
    {
      when: onForm,
      target: '[data-tour="return-photos"]',
      icon: 'camera',
      title: 'Add photos',
      body: 'At least one photo is required. A clear shot of the part and its label speeds up approval. You can add up to 12.',
    },
    {
      when: onForm,
      target: '[data-tour="return-submit"]',
      icon: 'send',
      title: 'Submit',
      body: 'We will email you a confirmation, and you can follow progress under <b>My Returns</b>.',
    },
    {
      title: 'That’s all there is to it',
      body: 'Questions about a return? Your store’s details are in the store selector.',
      hero: 'success',
      nextLabel: 'Done',
    },
  ],
};
